import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, cashService, cashShopService, charactersService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import type { CashShopItemDto, CashShopCategoryDto } from '../types';
import '../styles/Store.css';

const ITEMS_PER_PAGE = 9;

const Store: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [user, setUser] = useState<{ account: string } | null>(null);
    const [balance, setBalance] = useState({ totalBalance: 0, cashBalance: 0, bonusBalance: 0 });
    const [categories, setCategories] = useState<CashShopCategoryDto[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [items, setItems] = useState<CashShopItemDto[]>([]);
    const [characters, setCharacters] = useState<Array<{ dbKey: number; name: string; class: string; level: number }>>([]);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hoveredItem, setHoveredItem] = useState<CashShopItemDto | null>(null);
    const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);

    useEffect(() => {
        const u = authService.getCurrentUser();
        if (!u?.account) {
            navigate('/login');
            return;
        }
        setUser(u);
    }, [navigate]);

    useEffect(() => {
        if (!user?.account) return;

        const load = async () => {
            setLoading(true);
            try {
                const [bal, catList, itemList, charList] = await Promise.all([
                    cashService.getBalance(user.account),
                    cashShopService.getCategories(),
                    cashShopService.getItems(selectedCategory ?? undefined),
                    charactersService.getMyCharacters(),
                ]);
                setBalance(bal);
                setCategories(Array.isArray(catList) ? catList : []);
                setItems(Array.isArray(itemList) ? itemList : []);
                const chars = Array.isArray(charList) ? charList : [];
                setCharacters(chars.map((c: any) => ({
                    dbKey: c.dbKey ?? c.DBKey,
                    name: c.name ?? c.Name ?? '',
                    class: c.class ?? c.Class ?? '',
                    level: c.level ?? c.Level ?? 0,
                })));
            } catch {
                setItems([]);
                setCharacters([]);
            } finally {
                setLoading(false);
            }
        };
        load();
        setCurrentPage(1);
    }, [user?.account, selectedCategory]);

    const handleBuy = async (item: CashShopItemDto, characterDbKey: number, count: number) => {
        if (!user?.account) return;
        const recId = item.recId ?? (item as any).RecId ?? '';
        if (!recId) return;
        setBuying(recId);
        setMessage(null);
        try {
            const res = await cashShopService.buy(recId, count, characterDbKey);
            if (res.success) {
                setMessage({ type: 'success', text: res.message || t('store.buySuccess') });
                const bal = await cashService.getBalance(user.account);
                setBalance(bal);
            } else {
                setMessage({ type: 'error', text: res.message || t('store.buyError') });
            }
        } catch (e: any) {
            setMessage({ type: 'error', text: e?.response?.data?.message || t('store.buyError') });
        } finally {
            setBuying(null);
        }
    };

    if (!user) {
        return (
            <div className="store-page">
                <div className="container">
                    <p className="store-login-required">{t('store.loading')}</p>
                </div>
            </div>
        );
    }

    const price = (item: CashShopItemDto) => (item.cashCost ?? (item as any).cashCost) > 0
        ? (item.cashCost ?? (item as any).cashCost)
        : (item.price ?? (item as any).price ?? 0);

    return (
        <div className="store-page">
            <div className="container">
                <header className="store-header">
                    <h1 className="store-title">{t('store.title')}</h1>
                    <div className="store-balance">
                        <span className="store-balance-label">{t('store.yourBalance')}:</span>
                        <span className="store-balance-value">{balance.totalBalance ?? 0} Cash</span>
                    </div>
                </header>

                {message && (
                    <div className={`store-message store-message-${message.type}`}>
                        {message.text}
                    </div>
                )}

                {loading ? (
                    <p className="store-loading">{t('store.loading')}</p>
                ) : (
                    <>
                        <section className="store-items-section">
                            <div className="store-section-head">
                                <h2 className="store-section-title">{t('store.items')}</h2>
                                <div className="store-category-tabs">
                                    <button
                                        type="button"
                                        className={`store-cat-tab ${selectedCategory === null ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(null)}
                                    >
                                        {t('store.allCategories')}
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            className={`store-cat-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                                            onClick={() => setSelectedCategory(cat.id)}
                                        >
                                            {cat.label ?? cat.id}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {items.length === 0 ? (
                                <p className="store-empty">{t('store.noItems')}</p>
                            ) : (
                                <>
                                <div className="store-grid">
                                    {items
                                        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                                        .map((item) => {
                                            const recId = item.recId ?? (item as any).RecId ?? '';
                                            const name = item.itemName ?? (item as any).ItemName ?? recId;
                                            const p = price(item);
                                            const cnt = item.count ?? (item as any).Count ?? 1;
                                            const iconFile = (item.iconFile ?? (item as any).iconFile ?? '').toString();
                                            const hasIcon = !!iconFile && iconFile !== 'default_icon';
                                            const descriptionHtml = (item.descriptionHtml ?? (item as any).descriptionHtml) as string | undefined;
                                            const descriptionTitle = item.descriptionTitle ?? (item as any).descriptionTitle ?? name;
                                            return (
                                                <div key={recId} className="store-card">
                                                    <div className="store-card-body">
                                                        {hasIcon && (
                                                            <div className="store-card-icon-wrapper">
                                                                <img
                                                                    src={`/assets/imgcash/${iconFile}.png`}
                                                                    alt={name}
                                                                    className="store-card-icon"
                                                                    onMouseEnter={(e) => {
                                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                                        setHoveredItem(item);
                                                                        setHoverPosition({
                                                                            x: rect.right + 12,
                                                                            y: rect.top,
                                                                        });
                                                                    }}
                                                                    onMouseLeave={() => {
                                                                        setHoveredItem((current) => (current?.recId === recId ? null : current));
                                                                        setHoverPosition((current) => (current ? null : current));
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                        <h3 className="store-card-name">{descriptionTitle || name}</h3>
                                                        <div className="store-card-meta">
                                                            <span className="store-card-badges">
                                                                {(item.isNew ?? (item as any).isNew) ? <span className="store-badge store-badge-new">NEW</span> : null}
                                                                {(item.isBest ?? (item as any).isBest) ? <span className="store-badge store-badge-best">BEST</span> : null}
                                                                {(item.isRecommand ?? (item as any).isRecommand) ? <span className="store-badge store-badge-recomm">REC</span> : null}
                                                            </span>
                                                            <span className="store-card-price">{p} Cash</span>
                                                            {cnt > 1 && <span className="store-card-count">x{cnt}</span>}
                                                        </div>
                                                    </div>
                                                <div className="store-card-actions">
                                                    {characters.length === 0 ? (
                                                        <p className="store-no-chars">{t('store.noCharacters')}</p>
                                                    ) : (
                                                        <BuyForm
                                                            fullItem={item}
                                                            recId={recId}
                                                            defaultCount={cnt}
                                                            price={p}
                                                            characters={characters}
                                                            buying={buying === recId}
                                                            onBuy={handleBuy}
                                                            t={t}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="store-pagination">
                                    <button
                                        type="button"
                                        className="store-page-btn"
                                        disabled={currentPage <= 1}
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    >
                                        {t('store.prevPage')}
                                    </button>
                                    <span className="store-page-info">
                                        {currentPage} / {Math.ceil(items.length / ITEMS_PER_PAGE) || 1}
                                    </span>
                                    <button
                                        type="button"
                                        className="store-page-btn"
                                        disabled={currentPage >= Math.ceil(items.length / ITEMS_PER_PAGE)}
                                        onClick={() => setCurrentPage((p) => Math.min(Math.ceil(items.length / ITEMS_PER_PAGE), p + 1))}
                                    >
                                        {t('store.nextPage')}
                                    </button>
                                </div>
                                </>
                            )}
                        </section>
                        {hoveredItem && hoveredItem.descriptionHtml && hoverPosition && (
                            <div
                                className="store-item-tooltip"
                                style={{ left: hoverPosition.x, top: hoverPosition.y }}
                            >
                                <div className="store-item-tooltip-inner">
                                    <h4 className="store-item-tooltip-title">
                                        {hoveredItem.descriptionTitle || hoveredItem.itemName || hoveredItem.recId}
                                    </h4>
                                    <div
                                        className="store-item-tooltip-body"
                                        dangerouslySetInnerHTML={{ __html: hoveredItem.descriptionHtml as string }}
                                    />
                                </div>
                            </div>
                        )}

                    </>
                )}

                <p className="store-back">
                    <Link to="/usercp" className="store-back-link">← {t('usercp.overview')}</Link>
                </p>
            </div>
        </div>
    );
};

function BuyForm({
    fullItem,
    recId,
    defaultCount,
    price,
    characters,
    buying,
    onBuy,
    t,
}: {
    fullItem: CashShopItemDto;
    recId: string;
    defaultCount: number;
    price: number;
    characters: Array<{ dbKey: number; name: string; class: string; level: number }>;
    buying: boolean;
    onBuy: (item: CashShopItemDto, characterDbKey: number, count: number) => void;
    t: (key: string) => string;
}) {
    const [charId, setCharId] = useState<number>(characters[0]?.dbKey ?? 0);
    const [count, setCount] = useState(defaultCount);

    useEffect(() => {
        if (characters.length && !characters.some((c) => c.dbKey === charId)) {
            setCharId(characters[0].dbKey);
        }
    }, [characters, charId]);

    const total = price * Math.max(1, count);
    return (
        <div className="store-buy-form">
            <select
                className="store-buy-char"
                value={charId}
                onChange={(e) => setCharId(Number(e.target.value))}
            >
                {characters.map((c) => (
                    <option key={c.dbKey} value={c.dbKey}>{c.name}</option>
                ))}
            </select>
            <input
                type="number"
                min={1}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)}
                className="store-buy-qty"
            />
            <span className="store-buy-total">{total} Cash</span>
            <button
                type="button"
                className="store-buy-btn"
                disabled={buying}
                onClick={() => {
                    const safeCount = Math.max(1, count);
                    const totalCash = price * safeCount;
                    const name =
                        (fullItem.descriptionTitle as string | undefined) ??
                        (fullItem as any).descriptionTitle ??
                        fullItem.itemName ??
                        (fullItem as any).ItemName ??
                        recId;

                    const confirmMessage =
                        `Você deseja comprar ${safeCount}x "${name}" ` +
                        `por ${totalCash} Cash para o personagem selecionado?`;

                    if (!window.confirm(confirmMessage)) {
                        return;
                    }

                    onBuy(fullItem, charId, safeCount);
                }}
            >
                {buying ? '...' : t('store.buy')}
            </button>
        </div>
    );
}

export default Store;
